import React from 'react';
import styled, { css } from 'styled-components';
import Link from 'components/core/link';

import { StarCheck } from 'src/assets/star-check';
import { OptionTile } from 'src/components/option-tile';
import { Option as BlankOptionTile } from 'src/components/option-tile/option.styles';

import { ComingSoonChip } from 'src/components/chips';
import { CollectionBadge } from '../components/collection-card-badge';
import { SpecialOfferRotator } from '../components/special-offer-rotator';
import HoverImageGallery from '../components/hover-image-gallery';
import { FullCardViewProps } from '../types';

const MAX_OPTIONS_TO_DISPLAY = 8;

/**
 * Pure presentational list card component.
 * Receives all data and callbacks via props -- no hooks or store access.
 */
export const FullCardView = ({
  product: { name, brandName, href, strainType, potency },
  image,
  badges: { isStaffPick, isSponsored, isSpecialOffer, isComingSoon, specialNames, hideSalesLanguage, collectionBadge },
  options: { items, totalCount, hasMultiple, shouldDisplayLabel },
  listPrice: { listPriceEnabled, hideStrikethrough, formatStandardPrice: formatPrice },
  layout: { hideBorder },
  onLinkClick,
  onOptionClick,
  containerRef,
  productIndex,
}: FullCardViewProps): JSX.Element => {
  const showTags = isStaffPick || isSpecialOffer || collectionBadge || isComingSoon;
  const numberOptionsToDisplay = totalCount > MAX_OPTIONS_TO_DISPLAY ? 7 : totalCount;

  return (
    <Wrapper ref={containerRef} $hideBorder={hideBorder} data-placement-index={productIndex}>
      <Link href={href} onClick={onLinkClick}>
        <StyledAnchor>
          <ImageWrapper>
            <HoverImageGallery {...image} alt={`Image of ${name} product`} height={104} width={104} />
          </ImageWrapper>

          <NameWrapper>
            {isSponsored && <Sponsored>Sponsored</Sponsored>}

            <Name>{name}</Name>

            {brandName && <Brand>{brandName}</Brand>}

            {(!!strainType || potency.length > 0) && (
              <Details>
                {strainType && <Strain>{strainType}</Strain>}

                {potency.length > 0 && (
                  <Potency>
                    {potency.map((p) => (
                      <div key={p}>{p}</div>
                    ))}
                  </Potency>
                )}
              </Details>
            )}

            {showTags && (
              <Tags>
                {(isStaffPick || isSpecialOffer || isComingSoon) && (
                  <TagGroup>
                    {isStaffPick && (
                      <StaffPick>
                        <StarCheck />
                        Staff Pick
                      </StaffPick>
                    )}

                    {isSpecialOffer && (
                      <SpecialOfferRotator specialNames={specialNames} hideSalesLanguage={hideSalesLanguage} />
                    )}

                    {isComingSoon && (
                      <ComingSoon>
                        <ComingSoonChip />
                      </ComingSoon>
                    )}
                  </TagGroup>
                )}

                {collectionBadge && <CollectionBadge {...collectionBadge} />}
              </Tags>
            )}
          </NameWrapper>
        </StyledAnchor>
      </Link>

      {!isComingSoon && (
        <OptionsWrapper>
          <Options $isNarrowLayout={numberOptionsToDisplay === 5 || numberOptionsToDisplay === 6}>
            {items
              .slice(0, numberOptionsToDisplay)
              .map(({ value, label, currentPrice, originalPrice, discountLabel }) => (
                <OptionTile
                  key={value}
                  label={label}
                  currentPrice={currentPrice}
                  originalPrice={originalPrice ? formatPrice(originalPrice) : null}
                  discountLabel={discountLabel}
                  onClick={() => onOptionClick(value)}
                  hasMultipleOptions={hasMultiple}
                  isOutlinedVariant={false}
                  shouldDisplayLabel={shouldDisplayLabel}
                  listPriceEnabled={listPriceEnabled}
                  hideStrikethrough={hideStrikethrough}
                />
              ))}

            {totalCount > MAX_OPTIONS_TO_DISPLAY && (
              <BlankOptionTile
                onClick={() => onOptionClick('')}
                data-testid='blank-option-tile'
                $blank
                $hasMultipleOptions
              >
                +{totalCount - numberOptionsToDisplay} more <br />
                options
              </BlankOptionTile>
            )}
          </Options>
        </OptionsWrapper>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $hideBorder?: boolean }>`
  border-top: 1px solid #e3e7e9;
  padding: 20px 0;

  ${({ $hideBorder }) =>
    $hideBorder &&
    css`
      border-top: none;
    `}

  @media screen and (min-width: 740px) {
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
`;

const NameWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledAnchor = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 20px;

  @media screen and (min-width: 740px) {
    padding-right: 10px;
  }
`;

const ImageWrapper = styled.div`
  flex-shrink: 0;
  height: 92px;
  width: 92px;

  @media screen and (min-width: 740px) {
    height: 104px;
    width: 104px;
  }

  img {
    display: block;
    max-height: 100%;
    max-width: 100%;
    object-fit: cover;
  }
`;

const Sponsored = styled.div`
  color: #828a8f;
  font-size: 10px;
  line-height: 1;
  margin-bottom: 6px;

  @media screen and (min-width: 740px) {
    margin-bottom: 4px;
  }
`;

const Name = styled.div`
  color: #121516;
  font-size: 14px;
  font-weight: 600;
  line-height: ${16 / 14};

  @media screen and (min-width: 740px) {
    font-size: 16px;
    line-height: ${18 / 16};
  }
`;

const Brand = styled.div`
  color: #646d72;
  font: 12px/1 var(--dct--customized-fonts-secondary);
  margin-top: 6px;

  @media screen and (min-width: 740px) {
    margin-top: 4px;
  }
`;

const Details = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const Strain = styled.div`
  background: #e3e7e9;
  border-radius: 12px;
  color: #2c3236;
  display: inline-block;
  font: 500 12px/1 var(--dct--customized-fonts-secondary);
  padding: 6px 8px;
`;

const Potency = styled.div`
  align-items: center;
  color: #646d72;
  display: flex;
  gap: 8px;
  font: 12px/1 var(--dct--customized-fonts-secondary);
`;

const Tags = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  line-height: ${13 / 12};
  margin-top: 12px;
  text-transform: var(--dct--customized-textTransforms-tags);
`;

const TagGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const Tag = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
`;

const ComingSoon = styled(Tag)`
  color: var(--dct--customized-colors-comingSoonTag);
  white-space: nowrap;
`;

const StaffPick = styled(Tag)`
  color: var(--dct--customized-colors-staffPickTag);
  white-space: nowrap;
`;

const OptionsWrapper = styled.div`
  @media screen and (max-width: 739px) {
    /* Shift to be full bleed of browser window */
    margin: 0 -16px;
    overflow-x: auto;
    /* Calculation is the negative margin + image size + gap */
    padding: 0 16px 0 ${16 + 92 + 20}px;
    -ms-overflow-style: none;
    scroll-behavior: smooth;
    scrollbar-width: none;

    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Options = styled.div<{ $isNarrowLayout?: boolean }>`
  align-items: center;
  display: flex;
  gap: 8px;

  @media screen and (max-width: 739px) {
    margin-top: 20px;
  }

  @media screen and (min-width: 740px) {
    flex-wrap: wrap;
    justify-content: flex-end;
    min-width: 312px;
    max-width: 312px;

    ${({ $isNarrowLayout }) =>
      $isNarrowLayout &&
      css`
        min-width: 240px;
        max-width: 240px;
      `}
  }
`;
